import React, { useState, useRef, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { defineMessages, FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import moment from 'moment'
import PropTypes from 'prop-types'
import { willParseInt } from 'src/helpers/customPropTypes'
import { selectCurrentMarketIndexHistoryByMonth } from 'src/store/selectors/marketRecord'
import { createNewEvent } from 'src/helpers/browser'
import { If } from 'src/Components'
import BaseChart from 'src/Components/shared/Chart'

import styles from './MarketSpotlightChart.module.scss'

const duration = parseInt(styles['transition-duration'], 10)

const Chart = ({ className, ...restProps }) => {
  const monthlyHistory = useSelector(selectCurrentMarketIndexHistoryByMonth)

  const chartRef = useRef()
  const chartElement = chartRef.current

  const [index, setIndex] = useState(-1)
  const data = useMemo(() => {
    if (chartElement) {
      return [
        ...chartElement.querySelectorAll('.datapoint circle[cx].point')
      ].map((el, i) => ({
        posX: parseInt(el.getAttribute('cx'), 10),
        posY: parseInt(el.getAttribute('cy'), 10),
        month: monthlyHistory[i].month,
        value: monthlyHistory[i].value,
        margin: 0
      }))
    }
    return []
  }, [chartElement, monthlyHistory, window.innerHeight, window.innerWidth]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (monthlyHistory.length && index === -1) {
      setIndex(monthlyHistory.length - 1)
    }
  }, [index, monthlyHistory])

  const handleChange = e => {
    const newIndex = e.target.value
    setIndex(newIndex)
  }

  if (data.length) {
    data[0].margin = '30px'
    data[data.length - 1].margin = '-38px'
  }

  return (
    <div className={className}>
      <div className={styles.container}>
        <BaseChart {...restProps} className={styles.chart} ref={chartRef} />
        <If condition={index > -1 && index < data.length}>
          <Selector
            index={index}
            data={data}
            onChange={handleChange}
            renderData={d => {
              const { posX, posY, value, month, margin } = d
              const dateString = moment(`${month}-02`).format("MMM 'YY")

              let marketKey
              if (value > 80) {
                marketKey = 'hottest'
              } else if (value > 60) {
                marketKey = 'hot'
              } else if (value > 40) {
                marketKey = 'balanced'
              } else if (value > 20) {
                marketKey = 'cold'
              } else {
                marketKey = 'coldest'
              }

              return (
                <div className={styles.tooltip}>
                  <div
                    className={classnames(
                      styles.bar,
                      styles[`bar-${marketKey}`]
                    )}
                    style={{ left: `${posX}px` }}
                  >
                    <div
                      className={styles.spacer}
                      style={{ height: `${posY}px` }}
                    />
                    <div className={styles.point} />
                    <div className={styles.triangle} />
                    <div className={styles.box} style={{ marginLeft: margin }}>
                      <div>{dateString}</div>
                      <div className={styles.message}>
                        <FormattedMessage {...marketMessages[marketKey]} />
                      </div>
                    </div>
                  </div>
                </div>
              )
            }}
          />
        </If>
      </div>
    </div>
  )
}

Chart.propTypes = {
  className: PropTypes.string
}

const Selector = ({ index, data, onChange, renderData }) => {
  const inputRef = useRef()
  const [count, setCount] = useState(index)

  useEffect(() => {
    const indexInt = parseInt(index, 10)
    if (indexInt !== count) {
      const newCount = indexInt - count > 0 ? count + 1 : count - 1
      setTimeout(() => setCount(newCount), duration + 10) // 10ms buffer so the point doesn't start drifitng away from the line at high speeds
    }
  }, [index, count])

  const handleMove = e => {
    try {
      let newIndex

      const offsetX = e.target.offsetParent.getBoundingClientRect().x
      if (offsetX === undefined) {
        return // Sorry old Edge...
      }
      const delta = e.pageX - offsetX - 40
      const nextIndex = data.findIndex(v => v.posX > delta)
      const prevIndex = Math.max(nextIndex - 1, 0)

      if (nextIndex === -1) {
        newIndex = data.length - 1
      } else if (data[prevIndex].posX !== undefined) {
        const absNextDelta = Math.abs(data[nextIndex].posX - delta)
        const absPrevDelta = Math.abs(data[prevIndex].posX - delta)
        newIndex = absNextDelta < absPrevDelta ? nextIndex : prevIndex
      }

      if (
        newIndex < data.length &&
        newIndex !== parseInt(index, 10) &&
        inputRef.current
      ) {
        const target = Object.assign({}, inputRef.current, {
          value: newIndex.toString()
        })
        const event = Object.assign({}, createNewEvent('change'), { target })
        onChange(event)
      }
    } catch {} // Can't handle the move? Fine, sucks for you.
  }

  return (
    <>
      {!!data[count] && renderData(data[count])}
      <div className={styles.selector} onMouseMove={handleMove}>
        <input
          ref={inputRef}
          tabIndex='-1'
          type='range'
          value={index}
          step='1'
          min='0'
          max={Math.max(0, data.length - 1)}
          onChange={onChange}
        />
      </div>
    </>
  )
}

Selector.propTypes = {
  index: willParseInt,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      posX: PropTypes.willParseInt // only non-generalized bit (handleMove), think about if/how other stuff might want to interact for sharing elsewhere
    })
  ),
  onChange: PropTypes.func,
  renderData: PropTypes.func
}

const marketMessages = defineMessages({
  coldest: {
    id: 'MarketSpotlight.chart.coldest',
    defaultMessage: 'Strong buyer’s advantage'
  },
  cold: {
    id: 'MarketSpotlight.chart.cold',
    defaultMessage: 'Slight buyer’s advantage'
  },
  balanced: {
    id: 'MarketSpotlight.chart.balanced',
    defaultMessage: 'Balanced market'
  },
  hot: {
    id: 'MarketSpotlight.chart.hot',
    defaultMessage: 'Slight seller’s advantage'
  },
  hottest: {
    id: 'MarketSpotlight.chart.hottest',
    defaultMessage: 'Strong seller’s advantage'
  }
})

export default Chart
